import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ICONS } from '../data/icons';

function makeMainIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:16px;height:16px;
      background:#6366f1;
      border:2.5px solid #fff;
      border-radius:50%;
      box-shadow:0 0 0 4px rgba(99,102,241,.25),0 2px 8px rgba(99,102,241,.5)
    "></div>`,
    iconSize: [16, 16], iconAnchor: [8, 8],
  });
}

function makePOIIcon(iconKey) {
  const svg = ICONS[iconKey]?.svg || ICONS.marker.svg;
  return L.divIcon({
    className: '',
    html: `<div style="
      width:30px;height:30px;
      background:#0d1117;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 3px 10px rgba(0,0,0,.35)
    "><svg viewBox="0 0 24 24" style="width:14px;height:14px;fill:#fff;transform:rotate(45deg)">${svg}</svg></div>`,
    iconSize: [30, 30], iconAnchor: [10, 30],
  });
}

export default function MapView({
  mainLatLng, radius, pois,
  onPOIDragEnd, focusPOIId, setFocusPOIId,
  mapRef: externalMapRef,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const mainMarkerRef = useRef(null);
  const radiusCircleRef = useRef(null);
  const poiMarkersRef = useRef({});

  // Init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const m = L.map(containerRef.current, { zoomControl: false }).setView([37.7749, -122.4194], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
      crossOrigin: 'anonymous',
    }).addTo(m);
    L.control.zoom({ position: 'bottomright' }).addTo(m);
    mapRef.current = m;
    if (externalMapRef) externalMapRef.current = m;
  }, [externalMapRef]);

  // Main marker + radius circle
  useEffect(() => {
    if (!mapRef.current || !mainLatLng) return;
    if (mainMarkerRef.current) mapRef.current.removeLayer(mainMarkerRef.current);
    if (radiusCircleRef.current) mapRef.current.removeLayer(radiusCircleRef.current);

    mainMarkerRef.current = L.marker([mainLatLng.lat, mainLatLng.lng], { icon: makeMainIcon() })
      .addTo(mapRef.current)
      .bindPopup('<strong style="font-family:Inter,sans-serif;font-size:12px">📍 Main Location</strong>')
      .openPopup();

    radiusCircleRef.current = L.circle([mainLatLng.lat, mainLatLng.lng], {
      radius, color: '#6366f1', fillColor: '#6366f1',
      fillOpacity: 0.06, weight: 1.5, dashArray: '5 5',
    }).addTo(mapRef.current);

    mapRef.current.setView([mainLatLng.lat, mainLatLng.lng], 15);
  }, [mainLatLng, radius]);

  // Radius circle update
  useEffect(() => {
    if (!mapRef.current || !mainLatLng || !radiusCircleRef.current) return;
    mapRef.current.removeLayer(radiusCircleRef.current);
    radiusCircleRef.current = L.circle([mainLatLng.lat, mainLatLng.lng], {
      radius, color: '#6366f1', fillColor: '#6366f1',
      fillOpacity: 0.06, weight: 1.5, dashArray: '5 5',
    }).addTo(mapRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius]);

  // POI markers
  useEffect(() => {
    if (!mapRef.current) return;
    const currentIds = new Set(pois.map(p => p.id));

    // Remove markers for deleted POIs
    Object.keys(poiMarkersRef.current).forEach(id => {
      if (!currentIds.has(Number(id))) {
        mapRef.current.removeLayer(poiMarkersRef.current[id]);
        delete poiMarkersRef.current[id];
      }
    });

    // Add/update markers
    pois.forEach((poi, i) => {
      const existing = poiMarkersRef.current[poi.id];
      const popupHtml = `<div style="font-family:Inter,sans-serif;font-size:12px">
        <div style="font-weight:600">${poi.name}</div>
        ${poi.distance !== undefined ? `<div style="color:#9ca3af;margin-top:2px;font-size:11px">${(poi.distance < 1 ? Math.round(poi.distance*1000)+' m' : poi.distance.toFixed(2)+' km')} away</div>` : ''}
      </div>`;

      if (!existing) {
        const marker = L.marker([poi.lat, poi.lng], {
          icon: makePOIIcon(poi.iconKey),
          draggable: true,
        }).addTo(mapRef.current).bindPopup(popupHtml);

        marker.on('dragend', (e) => {
          const p = e.target.getLatLng();
          onPOIDragEnd(poi.id, p.lat, p.lng);
        });

        poiMarkersRef.current[poi.id] = marker;
      } else {
        // Update icon if changed
        existing.setIcon(makePOIIcon(poi.iconKey));
        existing.setPopupContent(popupHtml);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pois]);

  // Focus POI
  useEffect(() => {
    if (!focusPOIId || !mapRef.current) return;
    const poi = pois.find(p => p.id === focusPOIId);
    if (poi) {
      mapRef.current.setView([poi.lat, poi.lng], 17);
      poiMarkersRef.current[poi.id]?.openPopup();
    }
    setFocusPOIId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPOIId]);

  return (
    <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
      {!mainLatLng && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', background: 'var(--bg)',
          color: 'var(--text-3)', gap: 12, zIndex: 10, pointerEvents: 'none',
        }}>
          <div style={{
            width: 64, height: 64, background: 'var(--surface)', border: '1.5px solid var(--border)',
            borderRadius: 'var(--r-xl)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, boxShadow: 'var(--shadow-md)',
          }}>🗺️</div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Enter an address to begin</div>
          <div style={{ fontSize: 12 }}>Points of interest will appear here</div>
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
